import { defineCopy } from '../pageCopy';

/**
 * Copy for the job application flow (/apply, /ar/apply, /ckb/apply).
 *
 * Placeholders in braces, such as {name}, are filled at render time by
 * `fillCopy` / `richCopy` in TypeformApply. The `en` block is the exact
 * English text that shipped before translation, except the salary example,
 * which used an en dash and now reads "800,000 to 1,000,000".
 */
const en = {
  meta: {
    title: 'Join Our Team',
    description:
      'Join the Datacode team! We are hiring talented developers, designers, and AI specialists. Apply now for career opportunities in Erbil.',
    ogTitle: 'Join Our Team | Datacode',
    ogDescription:
      'Join the Datacode team! We are hiring talented developers, designers, and AI specialists.',
  },
  chrome: {
    brandAria: 'Datacode home',
    allCareers: 'All careers',
    introEyebrow: 'GOOD PEOPLE. GREAT WORK.',
    introTitleA: 'your next',
    introTitleB: 'chapter.',
    introLine1: 'A little about you.',
    introLine2: 'A lot of possibility.',
    footLeft: 'DATACODE / CAREERS',
    footRight: 'ERBIL, KURDISTAN',
  },
  eyebrow: {
    know: 'LET’S GET TO KNOW YOU',
    optional: 'OPTIONAL / A LITTLE EXTRA',
    review: 'REVIEW & SEND',
    cv: 'THE FINISHING TOUCH',
  },
  labels: {
    position: 'Role',
    name: 'Full name',
    email: 'Email address',
    phone: 'Phone number',
    city: 'City',
    salaryExpectation: 'Expected monthly salary',
    message: 'A little about you',
    linkedin: 'LinkedIn',
    portfolio: 'Portfolio or website',
    cv: 'CV',
  },
  questions: {
    position: 'Where would you like to make a difference?',
    name: 'First, what’s your name?',
    email: 'Where can we reach you?',
    phone: 'And your phone number?',
    city: 'Which city do you call home?',
    salaryExpectation: 'What salary do you have in mind?',
    message: 'Tell us a little about yourself.',
    links: 'Let your work speak.',
    cv: 'One last thing. Your CV.',
    review: 'Looking good. Make it yours.',
  },
  placeholders: {
    name: 'Your full name',
    email: 'you@example.com',
    phone: '+964 750 000 0000',
    city: 'e.g. Erbil',
    salaryExpectation: 'e.g. 800,000 to 1,000,000 IQD / month',
    message: 'A few sentences, in your own words…',
    linkedin: 'linkedin.com/in/your-name',
    portfolio: 'your-website.com',
  },
  hints: {
    email: 'We’ll use this to get in touch about your application.',
    city: 'Our team works on-site in Erbil, Kurdistan.',
    salaryExpectation:
      'Include your currency and monthly amount. A range is welcome.',
    messageRole:
      'What would you bring to the {role} role? Tell us about your experience and how you work independently.',
    messageTeam:
      'What would you bring to the team? Tell us about your experience and how you work independently.',
    charCount: '{counter} characters',
    links: 'Share a profile or a project you’re proud of. You can skip this.',
    cv: 'PDF or Word document, up to 5 MB.',
    cvRestored:
      'Your answers are restored. For your privacy, please select your CV again.',
    review: 'Take a moment to check your details. You can change anything below.',
    privacy:
      'Your application and CV will be shared with Datacode’s hiring team, including through our private Telegram notification chat, to review your application.',
    sessionNote: 'Your answers stay in this tab while you apply.',
  },
  upload: {
    aria: 'Upload CV',
    title: 'Bring your experience along.',
    subtitle: 'Choose a file from your phone or computer.',
    ready: '{size} MB · Ready to send',
    choose: 'Choose CV',
    replace: 'Replace CV',
    remove: 'Remove file',
  },
  review: {
    notAdded: 'Not added',
    cvMissing: 'Please select your CV again',
    edit: 'Edit',
    editField: 'Edit {field}',
    editCv: 'Edit CV',
  },
  buttons: {
    back: 'Back',
    cancelEdit: 'Cancel edit',
    skip: 'Skip',
    continue: 'Continue',
    saveAnswer: 'Save answer',
    send: 'Send application',
    tryAgain: 'Try again',
    resume: 'Resume application',
    startFresh: 'Start fresh',
    backToCareers: 'Back to careers',
  },
  progress: {
    yourApplication: 'YOUR APPLICATION',
    stepAria: 'Step {step} of {total}',
    barAria: 'Application progress',
    openRoles: 'Open roles',
  },
  states: {
    loading: 'Opening your application…',
    loadFailTitle: 'Let’s try that again.',
    loadFailBody:
      'We couldn’t load the open roles. Check your connection and retry.',
    noRolesTitle: 'No open roles right now.',
    noRolesBody: 'Please check back for our next opening.',
    draftEyebrow: 'WELCOME BACK',
    draftTitle: 'Pick up where you left off.',
    draftBody:
      'Your answers are saved in this browser tab. You’ll need to choose your CV again.',
    preparing: 'Preparing your CV…',
    sending: 'Sending your application… {percent}%',
    saving: 'Saving your application…',
    keepOpen: 'Please keep this page open.',
    honeypot: 'Leave this empty',
  },
  success: {
    eyebrow: 'YOUR NEXT CHAPTER STARTS HERE',
    title: 'Application received.',
    body: 'Thanks, {name}. Your application for {role} is saved. Our team will review it and contact you if your experience matches the role.',
    reference: 'Your reference',
  },
  errors: {
    required: 'Please answer this question to continue.',
    url: 'Use a valid website address, such as https://example.com.',
    email: 'Please enter a valid email address.',
    phone: 'Please enter a valid phone number, including your country code.',
    minChars: 'Please enter at least two characters.',
    shortMessage: 'A couple of sentences about your experience is enough.',
    closedRole: 'That role is no longer available. Choose an open role below.',
    draftRoleClosed: 'Your previous role is closed. Please choose an open role.',
    chooseCv: 'Choose your CV before continuing.',
    cvType: 'Choose a PDF or Word document.',
    cvSize: 'Choose a non-empty file up to 5 MB.',
    cvName: 'Please shorten the file name to 180 characters or fewer.',
    cvAgain: 'Please select your CV again before submitting.',
    submitFailed: 'We could not save your application. Please retry.',
    readCv: 'We could not read your CV. Please select it again.',
    saveFailed: 'We could not save your application. Please try again.',
    unconfirmed:
      'We could not confirm your application. Please retry; this will not create a second application.',
    interrupted:
      'Your connection was interrupted. Your answers are still here. Retry to confirm your application.',
  },
  /** Job titles by position value. Values are the backend slugs and never change. */
  positions: {
    'sales-specialist': 'Sales Specialist',
    'pr-sales-specialist': 'PR & Sales Specialist',
    'marketing-specialist': 'Marketing Specialist',
    'frontend-developer': 'Frontend Developer',
    'backend-developer': 'Backend Developer',
    'fullstack-developer': 'Full Stack Developer',
    'mobile-developer': 'Mobile Developer',
    'ui-ux-designer': 'UI/UX Designer',
    'graphic-designer': 'Graphic Designer',
    'project-manager': 'Project Manager',
  },
};

export type ApplyCopy = typeof en;

const ar: ApplyCopy = {
  meta: {
    title: 'انضم لفريقنا',
    description:
      'انضم لفريق Datacode! نبحث عن مطورين ومصممين ومختصين بالذكاء الاصطناعي من أصحاب الموهبة. قدم الآن على فرص العمل بأربيل.',
    ogTitle: 'انضم لفريقنا | Datacode',
    ogDescription:
      'انضم لفريق Datacode! نبحث عن مطورين ومصممين ومختصين بالذكاء الاصطناعي من أصحاب الموهبة.',
  },
  chrome: {
    brandAria: 'Datacode، الصفحة الرئيسية',
    allCareers: 'كل الوظائف',
    introEyebrow: 'ناس زينة. شغل عظيم.',
    introTitleA: 'فصلك',
    introTitleB: 'الجاي.',
    introLine1: 'شوية عنك.',
    introLine2: 'وفرص كثيرة قدامك.',
    footLeft: 'Datacode · الوظائف',
    footRight: 'أربيل، كوردستان',
  },
  eyebrow: {
    know: 'خلينا نتعرف عليك',
    optional: 'اختياري / لمسة إضافية',
    review: 'مراجعة وإرسال',
    cv: 'اللمسة الأخيرة',
  },
  labels: {
    position: 'الوظيفة',
    name: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    phone: 'رقم الهاتف',
    city: 'المدينة',
    salaryExpectation: 'الراتب الشهري المتوقع',
    message: 'نبذة عنك',
    linkedin: 'LinkedIn',
    portfolio: 'الموقع أو معرض الأعمال',
    cv: 'السيرة الذاتية',
  },
  questions: {
    position: 'وين تحب تصنع فرق؟',
    name: 'أول شي، شنو اسمك؟',
    email: 'شلون نكدر نتواصل وياك؟',
    phone: 'وشنو رقم هاتفك؟',
    city: 'أي مدينة تسكن بيها؟',
    salaryExpectation: 'شنو الراتب اللي براسك؟',
    message: 'حدثنا شوية عن نفسك.',
    links: 'خلي شغلك يتكلم عنك.',
    cv: 'آخر شي. سيرتك الذاتية.',
    review: 'شكلها حلو. عدل عليها على كيفك.',
  },
  placeholders: {
    name: 'اسمك الكامل',
    email: 'you@example.com',
    phone: '+964 750 000 0000',
    city: 'مثلاً أربيل',
    salaryExpectation: 'مثلاً 800,000 إلى 1,000,000 دينار / شهر',
    message: 'كم جملة، بأسلوبك أنت…',
    linkedin: 'linkedin.com/in/your-name',
    portfolio: 'your-website.com',
  },
  hints: {
    email: 'نستخدمه حتى نتواصل وياك بخصوص طلبك.',
    city: 'فريقنا يداوم حضورياً بأربيل، كوردستان.',
    salaryExpectation:
      'اكتب العملة والمبلغ الشهري. وتكدر تكتب نطاق بعد.',
    messageRole:
      'شنو رح تضيف لوظيفة {role}؟ حدثنا عن خبرتك وشلون تشتغل باستقلالية.',
    messageTeam:
      'شنو رح تضيف للفريق؟ حدثنا عن خبرتك وشلون تشتغل باستقلالية.',
    charCount: '{counter} حرف',
    links: 'شارك حساب أو مشروع تفتخر بيه. تكدر تتخطى هذي الخطوة.',
    cv: 'ملف PDF أو مستند Word، لحد 5 ميغابايت.',
    cvRestored:
      'رجعنا إجاباتك. حفاظاً على خصوصيتك، اختار سيرتك الذاتية من جديد.',
    review: 'خذ لحظة وراجع بياناتك. تكدر تعدل أي شي تحت.',
    privacy:
      'طلبك وسيرتك الذاتية راح يوصلون لفريق التوظيف في Datacode، ومنها عبر مجموعة Telegram الخاصة بالإشعارات، حتى يراجعون طلبك.',
    sessionNote: 'إجاباتك تبقى بهذا التبويب طول ما تقدم.',
  },
  upload: {
    aria: 'رفع السيرة الذاتية',
    title: 'جيب خبرتك وياك.',
    subtitle: 'اختار ملف من موبايلك أو من الكمبيوتر.',
    ready: '{size} ميغابايت · جاهز للإرسال',
    choose: 'اختيار السيرة الذاتية',
    replace: 'استبدال السيرة الذاتية',
    remove: 'حذف الملف',
  },
  review: {
    notAdded: 'ما انضاف',
    cvMissing: 'اختار سيرتك الذاتية من جديد',
    edit: 'تعديل',
    editField: 'تعديل {field}',
    editCv: 'تعديل السيرة الذاتية',
  },
  buttons: {
    back: 'رجوع',
    cancelEdit: 'إلغاء التعديل',
    skip: 'تخطي',
    continue: 'متابعة',
    saveAnswer: 'حفظ الإجابة',
    send: 'إرسال الطلب',
    tryAgain: 'حاول مرة ثانية',
    resume: 'استئناف الطلب',
    startFresh: 'ابدأ من جديد',
    backToCareers: 'رجوع للوظائف',
  },
  progress: {
    yourApplication: 'طلبك',
    stepAria: 'الخطوة {step} من {total}',
    barAria: 'تقدم الطلب',
    openRoles: 'الوظائف المتاحة',
  },
  states: {
    loading: 'جاري فتح طلبك…',
    loadFailTitle: 'خلينا نحاول مرة ثانية.',
    loadFailBody:
      'ما كدرنا نحمل الوظائف المتاحة. تأكد من الاتصال وحاول مرة ثانية.',
    noRolesTitle: 'ماكو وظائف شاغرة هسة.',
    noRolesBody: 'رجع تشوفنا بعدين، يمكن تنفتح فرصة جديدة.',
    draftEyebrow: 'أهلاً بعودتك',
    draftTitle: 'كمل من وين وقفت.',
    draftBody:
      'إجاباتك محفوظة بهذا التبويب من المتصفح. راح تحتاج تختار سيرتك الذاتية من جديد.',
    preparing: 'جاري تجهيز سيرتك الذاتية…',
    sending: 'جاري إرسال طلبك… {percent}%',
    saving: 'جاري حفظ طلبك…',
    keepOpen: 'خلي هذي الصفحة مفتوحة.',
    honeypot: 'اترك هذا الحقل فارغ',
  },
  success: {
    eyebrow: 'فصلك الجاي يبدي من هنا',
    title: 'وصلنا طلبك.',
    body: 'شكراً {name}. طلبك لوظيفة {role} انحفظ. فريقنا راح يراجعه ويتواصل وياك إذا خبرتك تناسب الوظيفة.',
    reference: 'رقمك المرجعي',
  },
  errors: {
    required: 'جاوب على هذا السؤال حتى تكمل.',
    url: 'اكتب رابط موقع صحيح، مثل https://example.com',
    email: 'اكتب بريد إلكتروني صحيح.',
    phone: 'اكتب رقم هاتف صحيح مع رمز الدولة.',
    minChars: 'اكتب حرفين على الأقل.',
    shortMessage: 'كم جملة عن خبرتك تكفي.',
    closedRole: 'هذي الوظيفة ما بقت متاحة. اختار وظيفة من المتاحة تحت.',
    draftRoleClosed: 'الوظيفة اللي بديت عليها سابقاً انسدت. اختار وظيفة متاحة.',
    chooseCv: 'اختار سيرتك الذاتية قبل ما تكمل.',
    cvType: 'اختار ملف PDF أو مستند Word.',
    cvSize: 'اختار ملف مو فارغ وحجمه لحد 5 ميغابايت.',
    cvName: 'قصّر اسم الملف إلى 180 حرف أو أقل.',
    cvAgain: 'اختار سيرتك الذاتية من جديد قبل الإرسال.',
    submitFailed: 'ما كدرنا نحفظ طلبك. حاول مرة ثانية.',
    readCv: 'ما كدرنا نقرأ سيرتك الذاتية. اختارها من جديد.',
    saveFailed: 'ما كدرنا نحفظ طلبك. حاول مرة ثانية.',
    unconfirmed:
      'ما كدرنا نتأكد من طلبك. أعد المحاولة، وما راح ينفتح طلب ثاني.',
    interrupted:
      'انقطع الاتصال. إجاباتك لسه موجودة. أعد المحاولة حتى نتأكد من طلبك.',
  },
  positions: {
    'sales-specialist': 'أخصائي مبيعات',
    'pr-sales-specialist': 'أخصائي علاقات عامة ومبيعات',
    'marketing-specialist': 'أخصائي تسويق',
    'frontend-developer': 'مطور واجهات أمامية',
    'backend-developer': 'مطور أنظمة خلفية',
    'fullstack-developer': 'مطور متكامل',
    'mobile-developer': 'مطور تطبيقات موبايل',
    'ui-ux-designer': 'مصمم واجهات وتجربة مستخدم',
    'graphic-designer': 'مصمم جرافيك',
    'project-manager': 'مدير مشاريع',
  },
};

const ckb: ApplyCopy = {
  meta: {
    title: 'بەشداری تیمەکەمان بکە',
    description:
      'بەشداری تیمی Datacode بکە! بەدوای پەرەپێدەر، دیزاینەر و شارەزایانی زیرەکی دەستکرددا دەگەڕێین. ئێستا داواکاری بکە بۆ هەلی کار لە هەولێر.',
    ogTitle: 'بەشداری تیمەکەمان بکە | Datacode',
    ogDescription:
      'بەشداری تیمی Datacode بکە! بەدوای پەرەپێدەر، دیزاینەر و شارەزایانی زیرەکی دەستکرددا دەگەڕێین.',
  },
  chrome: {
    brandAria: 'Datacode، لاپەڕەی سەرەکی',
    allCareers: 'هەموو هەلەکانی کار',
    introEyebrow: 'کەسی باش. کاری مەزن.',
    introTitleA: 'بەشی دواتری',
    introTitleB: 'ژیانت.',
    introLine1: 'کەمێک دەربارەی تۆ.',
    introLine2: 'زۆر دەرفەت لە پێشتە.',
    footLeft: 'Datacode · هەلی کار',
    footRight: 'هەولێر، کوردستان',
  },
  eyebrow: {
    know: 'با بتناسین',
    optional: 'ئارەزوومەندانە / کەمێک زیاتر',
    review: 'پیاچوونەوە و ناردن',
    cv: 'دوایین بەش',
  },
  labels: {
    position: 'پۆستی کار',
    name: 'ناوی تەواو',
    email: 'ئیمەیڵ',
    phone: 'ژمارەی تەلەفۆن',
    city: 'شار',
    salaryExpectation: 'مووچەی مانگانەی چاوەڕوانکراو',
    message: 'کەمێک دەربارەی خۆت',
    linkedin: 'LinkedIn',
    portfolio: 'ماڵپەڕ یان پۆرتفۆلیۆ',
    cv: 'ژیاننامە',
  },
  questions: {
    position: 'حەز دەکەیت لە کوێ جیاوازی دروست بکەیت؟',
    name: 'یەکەم شت، ناوت چییە؟',
    email: 'چۆن پەیوەندیت پێوە بکەین؟',
    phone: 'ژمارەی تەلەفۆنت چییە؟',
    city: 'لە کام شار دەژیت؟',
    salaryExpectation: 'چ مووچەیەکت لە مێشکدایە؟',
    message: 'کەمێک دەربارەی خۆت بۆمان بنووسە.',
    links: 'با کارەکانت خۆیان قسە بکەن.',
    cv: 'دوایین شت. ژیاننامەکەت.',
    review: 'زۆر باشە. بیخوێنەرەوە و ئەگەر ویستت دەستکاری بکە.',
  },
  placeholders: {
    name: 'ناوی تەواوت',
    email: 'you@example.com',
    phone: '+964 750 000 0000',
    city: 'بۆ نموونە هەولێر',
    salaryExpectation: 'بۆ نموونە 800,000 بۆ 1,000,000 دینار / مانگ',
    message: 'چەند رستەیەک، بە شێوازی خۆت…',
    linkedin: 'linkedin.com/in/your-name',
    portfolio: 'your-website.com',
  },
  hints: {
    email: 'بۆ پەیوەندیکردن پێت دەربارەی داواکارییەکەت بەکاری دەهێنین.',
    city: 'تیمەکەمان لە شوێنی کار لە هەولێری کوردستان کار دەکات.',
    salaryExpectation:
      'دراو و بڕی مانگانە بنووسە. دەتوانیت ڕێژەیەکیش بنووسیت.',
    messageRole:
      'چی دەخەیتە سەر پۆستی {role}؟ دەربارەی ئەزموونەکەت و چۆنیەتی کارکردنی سەربەخۆت بۆمان بنووسە.',
    messageTeam:
      'چی دەخەیتە سەر تیمەکە؟ دەربارەی ئەزموونەکەت و چۆنیەتی کارکردنی سەربەخۆت بۆمان بنووسە.',
    charCount: '{counter} پیت',
    links:
      'پرۆفایلێک یان پرۆژەیەک کە شانازی پێوە دەکەیت هاوبەش بکە. دەتوانیت بیبەزێنیت.',
    cv: 'فایلی PDF یان Word، تا 5 مێگابایت.',
    cvRestored:
      'وەڵامەکانت گەڕێندرانەوە. بۆ پاراستنی نهێنیت، تکایە ژیاننامەکەت دووبارە هەڵبژێرە.',
    review:
      'کەمێک ئەم زانیاریانە بپشکنە. دەتوانیت هەر شتێک لە خوارەوە بگۆڕیت.',
    privacy:
      'داواکاری و ژیاننامەکەت لەگەڵ تیمی دامەزراندنی Datacode هاوبەش دەکرێت، لەوانە لە ڕێگەی گروپی تایبەتی ئاگادارکردنەوەی Telegram، بۆ ئەوەی داواکارییەکەت بپشکنن.',
    sessionNote: 'وەڵامەکانت تا داواکاری دەکەیت لەم تابەدا دەمێننەوە.',
  },
  upload: {
    aria: 'بارکردنی ژیاننامە',
    title: 'ئەزموونەکەتمان بۆ بهێنە.',
    subtitle: 'فایلێک لە مۆبایل یان کۆمپیوتەرەکەت هەڵبژێرە.',
    ready: '{size} مێگابایت · ئامادەیە بۆ ناردن',
    choose: 'هەڵبژاردنی ژیاننامە',
    replace: 'گۆڕینی ژیاننامە',
    remove: 'سڕینەوەی فایل',
  },
  review: {
    notAdded: 'زیاد نەکراوە',
    cvMissing: 'تکایە ژیاننامەکەت دووبارە هەڵبژێرە',
    edit: 'دەستکاری',
    editField: 'دەستکاری {field}',
    editCv: 'دەستکاری ژیاننامە',
  },
  buttons: {
    back: 'گەڕانەوە',
    cancelEdit: 'هەڵوەشاندنەوە',
    skip: 'بیبەزێنە',
    continue: 'بەردەوام بە',
    saveAnswer: 'پاشەکەوتکردن',
    send: 'ناردنی داواکاری',
    tryAgain: 'دووبارە هەوڵبدەرەوە',
    resume: 'بەردەوامبوون لە داواکاری',
    startFresh: 'لە سەرەتاوە دەست پێبکە',
    backToCareers: 'گەڕانەوە بۆ هەلەکانی کار',
  },
  progress: {
    yourApplication: 'داواکارییەکەت',
    stepAria: 'هەنگاوی {step} لە {total}',
    barAria: 'پێشکەوتنی داواکاری',
    openRoles: 'هەلە کارییە بەردەستەکان',
  },
  states: {
    loading: 'داواکارییەکەت دەکرێتەوە…',
    loadFailTitle: 'با دووبارە هەوڵ بدەینەوە.',
    loadFailBody:
      'نەمانتوانی هەلە کارییە بەردەستەکان بار بکەین. پەیوەندی ئینتەرنێتەکەت بپشکنە و دووبارە هەوڵبدەرەوە.',
    noRolesTitle: 'ئێستا هیچ هەلێکی کار بەردەست نییە.',
    noRolesBody: 'تکایە دواتر سەردانمان بکە بۆ هەلی نوێ.',
    draftEyebrow: 'بەخێربێیتەوە',
    draftTitle: 'لەو شوێنەوە بەردەوام بە کە وەستایت.',
    draftBody:
      'وەڵامەکانت لەم تابی وێبگەڕەدا پاشەکەوت کراون. پێویستە ژیاننامەکەت دووبارە هەڵبژێریت.',
    preparing: 'ژیاننامەکەت ئامادە دەکرێت…',
    sending: 'داواکارییەکەت دەنێردرێت… {percent}%',
    saving: 'داواکارییەکەت پاشەکەوت دەکرێت…',
    keepOpen: 'تکایە ئەم لاپەڕەیە کراوە بهێڵەرەوە.',
    honeypot: 'ئەمە بەتاڵ بهێڵەرەوە',
  },
  success: {
    eyebrow: 'بەشی دواتری ژیانت لێرەوە دەست پێدەکات',
    title: 'داواکارییەکەت گەیشت.',
    body: 'سوپاس {name}. داواکارییەکەت بۆ پۆستی {role} پاشەکەوت کرا. تیمەکەمان دەیبینێتەوە و ئەگەر ئەزموونەکەت لەگەڵ پۆستەکە بگونجێت پەیوەندیت پێوە دەکات.',
    reference: 'ژمارەی سەرچاوەکەت',
  },
  errors: {
    required: 'تکایە وەڵامی ئەم پرسیارە بدەرەوە تا بەردەوام بیت.',
    url: 'ناونیشانێکی ماڵپەڕی دروست بنووسە، وەک https://example.com',
    email: 'تکایە ئیمەیڵێکی دروست بنووسە.',
    phone: 'تکایە ژمارەی تەلەفۆنێکی دروست بنووسە، لەگەڵ کۆدی وڵات.',
    minChars: 'تکایە لانی کەم دوو پیت بنووسە.',
    shortMessage: 'چەند رستەیەک دەربارەی ئەزموونەکەت بەسە.',
    closedRole: 'ئەم هەلە کارییە چیتر بەردەست نییە. یەکێک لە هەلە بەردەستەکانی خوارەوە هەڵبژێرە.',
    draftRoleClosed: 'ئەو پۆستەی پێشتر هەڵتبژاردبوو داخراوە. تکایە پۆستێکی بەردەست هەڵبژێرە.',
    chooseCv: 'پێش بەردەوامبوون ژیاننامەکەت هەڵبژێرە.',
    cvType: 'فایلی PDF یان Word هەڵبژێرە.',
    cvSize: 'فایلێکی نەبەتاڵ هەڵبژێرە کە تا 5 مێگابایت بێت.',
    cvName: 'تکایە ناوی فایلەکە بکە بە 180 پیت یان کەمتر.',
    cvAgain: 'تکایە پێش ناردن ژیاننامەکەت دووبارە هەڵبژێرە.',
    submitFailed: 'نەمانتوانی داواکارییەکەت پاشەکەوت بکەین. تکایە دووبارە هەوڵبدەرەوە.',
    readCv: 'نەمانتوانی ژیاننامەکەت بخوێنینەوە. تکایە دووبارە هەڵیبژێرە.',
    saveFailed: 'نەمانتوانی داواکارییەکەت پاشەکەوت بکەین. تکایە دووبارە هەوڵبدەرەوە.',
    unconfirmed:
      'نەمانتوانی داواکارییەکەت پشتڕاست بکەینەوە. تکایە دووبارە هەوڵبدەرەوە، ئەمە داواکارییەکی دووەم دروست ناکات.',
    interrupted:
      'پەیوەندییەکەت پچڕا. وەڵامەکانت هەر ماون. دووبارە هەوڵبدەرەوە بۆ پشتڕاستکردنەوەی داواکارییەکەت.',
  },
  positions: {
    'sales-specialist': 'شارەزای فرۆش',
    'pr-sales-specialist': 'شارەزای پەیوەندی گشتی و فرۆش',
    'marketing-specialist': 'شارەزای بازاڕگەری',
    'frontend-developer': 'پەرەپێدەری ڕووکار',
    'backend-developer': 'پەرەپێدەری سیستەمی پشتەوە',
    'fullstack-developer': 'پەرەپێدەری تەواوی سیستەم',
    'mobile-developer': 'پەرەپێدەری ئەپی مۆبایل',
    'ui-ux-designer': 'دیزاینەری ڕووکار و ئەزموونی بەکارهێنەر',
    'graphic-designer': 'دیزاینەری گرافیک',
    'project-manager': 'بەڕێوەبەری پرۆژە',
  },
};

export const applyCopy = defineCopy(en, ar, ckb);
